import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';

export default function References() {
  const [references, setReferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        let docs = [];
        try {
          const q = query(collection(db, 'references'), orderBy('order', 'asc'));
          const snap = await getDocs(q);
          docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        } catch {
          const snap = await getDocs(collection(db, 'references'));
          docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        }
        const active = docs.filter(d => d.active);
        setReferences(active);
        if (active.length) setSelectedId(active[0].id);
      } catch {
        setReferences([]);
      }
      setLoading(false);
    };
    load();
  }, []);

  const selected = references.find(r => r.id === selectedId);

  const handleDownload = (ref) => {
    const link = document.createElement('a');
    link.href = ref.fileUrl;
    link.download = ref.downloadName || 'reference.pdf';
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFullscreen = () => {
    if (selected) window.open(selected.fileUrl, '_blank');
  };

  if (loading) {
    return (
      <div>
        <div className="page-heading">References</div>
        <div className="page-sub">Loading reference materials…</div>
      </div>
    );
  }

  if (!references.length) {
    return (
      <div>
        <div className="page-heading">References</div>
        <div className="page-sub">No reference materials are available right now. Please check back soon.</div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-heading">References</div>
      <div className="page-sub">
        Quick-access reference materials for the From Curious to Capable program. Read them in the portal or download your own copy to keep.
      </div>

      <div style={styles.grid}>
        {references.map(ref => {
          const isActive = ref.id === selectedId;
          return (
            <div
              key={ref.id}
              style={{ ...styles.card, ...(isActive ? styles.cardActive : {}) }}
              onClick={() => setSelectedId(ref.id)}
            >
              <div style={styles.cardHeader}>
                <span style={styles.tag}>{ref.tag}</span>
                <div style={styles.cardTitle}>{ref.title}</div>
                <div style={styles.cardSub}>{ref.subtitle}</div>
              </div>
              <div style={styles.cardBody}>
                <p style={styles.cardDesc}>{ref.description}</p>
                <div style={styles.meta}>
                  <div style={styles.metaInfo}>{ref.meta}</div>
                  <div style={styles.actions}>
                    <button
                      style={styles.btnGhost}
                      onClick={(e) => { e.stopPropagation(); handleDownload(ref); }}
                    >
                      ↓ Download
                    </button>
                    <button
                      style={styles.btnPrimary}
                      onClick={(e) => { e.stopPropagation(); setSelectedId(ref.id); }}
                    >
                      Open →
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div style={styles.viewer}>
        <div style={styles.viewerHeader}>
          <div style={styles.viewerTitleBlock}>
            <div style={styles.viewerIcon}>PDF</div>
            <div>
              <div style={styles.viewerTitle}>{selected.title}</div>
              <div style={styles.viewerSub}>From Curious to Capable · The Corporate Fixers</div>
            </div>
          </div>
          <div style={styles.viewerActions}>
            <button style={styles.btnOnDark} onClick={handleFullscreen}>⛶ Fullscreen</button>
            <button style={styles.btnOnDarkPrimary} onClick={() => handleDownload(selected)}>↓ Download</button>
          </div>
        </div>
        <div style={styles.viewerBody}>
          <iframe
            key={selected.id}
            src={selected.fileUrl}
            title={selected.title}
            style={styles.iframe}
          />
        </div>
      </div>
    </div>
  );
}

const styles = {
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '20px',
    marginBottom: '28px',
  },
  card: {
    background: '#FFFFFF',
    border: '1.5px solid #E8E6E2',
    cursor: 'pointer',
    transition: 'all 0.2s',
    overflow: 'hidden',
  },
  cardActive: {
    border: '1.5px solid #D4A847',
    boxShadow: '0 0 0 2px rgba(212,168,71,0.15)',
  },
  cardHeader: {
    background: '#1B2A4A',
    color: '#fff',
    padding: '24px 28px',
  },
  tag: {
    display: 'inline-block',
    background: 'rgba(212,168,71,0.18)',
    color: '#D4A847',
    fontSize: '10px',
    fontWeight: '600',
    padding: '4px 10px',
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    marginBottom: '14px',
  },
  cardTitle: {
    fontFamily: 'Playfair Display, serif',
    fontSize: '22px',
    fontWeight: '700',
    lineHeight: '1.2',
    marginBottom: '8px',
  },
  cardSub: {
    fontSize: '12px',
    color: 'rgba(255,255,255,0.6)',
    fontWeight: '300',
    fontStyle: 'italic',
  },
  cardBody: {
    padding: '22px 28px',
  },
  cardDesc: {
    fontSize: '13.5px',
    color: '#3D3D3D',
    lineHeight: '1.65',
    fontWeight: '300',
    marginBottom: '18px',
  },
  meta: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: '16px',
    borderTop: '1px solid #E8E6E2',
    flexWrap: 'wrap',
    gap: '12px',
  },
  metaInfo: {
    fontSize: '11px',
    color: '#8A8A8A',
    letterSpacing: '0.5px',
  },
  actions: {
    display: 'flex',
    gap: '8px',
  },
  btnPrimary: {
    fontFamily: 'DM Sans, sans-serif',
    fontSize: '12px',
    fontWeight: '600',
    padding: '9px 16px',
    border: 'none',
    cursor: 'pointer',
    background: '#1B2A4A',
    color: '#fff',
    letterSpacing: '0.3px',
  },
  btnGhost: {
    fontFamily: 'DM Sans, sans-serif',
    fontSize: '12px',
    fontWeight: '600',
    padding: '9px 16px',
    cursor: 'pointer',
    background: 'transparent',
    color: '#1B2A4A',
    border: '1.5px solid #E8E6E2',
    letterSpacing: '0.3px',
  },
  viewer: {
    background: '#fff',
    border: '1.5px solid #E8E6E2',
    overflow: 'hidden',
  },
  viewerHeader: {
    background: '#1B2A4A',
    color: '#fff',
    padding: '16px 24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '3px solid #D4A847',
    flexWrap: 'wrap',
    gap: '12px',
  },
  viewerTitleBlock: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
  },
  viewerIcon: {
    width: '38px',
    height: '38px',
    background: '#D4A847',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#1B2A4A',
    fontWeight: '800',
    fontSize: '11px',
    letterSpacing: '0.5px',
  },
  viewerTitle: {
    fontFamily: 'Playfair Display, serif',
    fontSize: '17px',
    fontWeight: '700',
  },
  viewerSub: {
    fontSize: '11px',
    color: 'rgba(255,255,255,0.55)',
    marginTop: '2px',
    fontStyle: 'italic',
  },
  viewerActions: {
    display: 'flex',
    gap: '8px',
  },
  btnOnDark: {
    fontFamily: 'DM Sans, sans-serif',
    fontSize: '12px',
    fontWeight: '600',
    padding: '9px 16px',
    cursor: 'pointer',
    background: 'rgba(255,255,255,0.08)',
    color: '#fff',
    border: '1px solid rgba(255,255,255,0.15)',
    letterSpacing: '0.3px',
  },
  btnOnDarkPrimary: {
    fontFamily: 'DM Sans, sans-serif',
    fontSize: '12px',
    fontWeight: '600',
    padding: '9px 16px',
    cursor: 'pointer',
    background: '#D4A847',
    color: '#1B2A4A',
    border: 'none',
    letterSpacing: '0.3px',
  },
  viewerBody: {
    background: '#F2F0EC',
    padding: 0,
    height: '900px',
  },
  iframe: {
    width: '100%',
    height: '100%',
    border: 'none',
    display: 'block',
  },
};
